import { Field, Label, Switch } from "@headlessui/react";
import clsx from "clsx";

interface HideUnlessLikedToggleProps {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}

/**
 * Opt-in discovery privacy toggle: only appear to people the user has already liked.
 */
export default function HideUnlessLikedToggle({ checked, disabled = false, onChange }: HideUnlessLikedToggleProps) {
  const statusLabel = checked ? "Hidden" : "Visible";
  const statusText = checked ? "You're currently hidden from discovery" : "You're currently visible in discovery";

  return (
    <Field className="flex flex-col gap-3" data-testid="hide-unless-liked-toggle-row">
      <Label className="block w-full text-sm text-gray-900" data-testid="hide-unless-liked-helper">
        {statusText}
      </Label>
      <div className="flex w-full flex-nowrap items-center justify-end gap-2">
        <span
          className="whitespace-nowrap text-xs font-normal text-gray-500"
          data-testid="hide-unless-liked-status"
          aria-live="polite"
        >
          {statusLabel}
        </span>
        <Switch
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          data-testid="hide-unless-liked-toggle"
          className={clsx(
            "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2",
            "data-[focus]:ring-2 data-[focus]:ring-gray-400",
            "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
            checked ? "bg-gray-600" : "bg-gray-300"
          )}
        >
          <span
            className={clsx(
              "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
              checked ? "translate-x-6" : "translate-x-1"
            )}
          />
        </Switch>
      </div>
    </Field>
  );
}
